import { useTranslation } from 'react-i18next';
import React, { useState, useEffect } from 'react';
import { axiosInstance } from '../utils/axiosInterceptor.js';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import * as queries from '../queries';
import Pagination from '../utils/Pagination';
import DataTable from '../utils/DataTable';
import SearchBarGraph from '../utils/SearchBarGraph';

function CustomerData() {
    const { t } = useTranslation();
    const [topCustomers, setTopCustomers] = useState([]);
    const apiUrl = process.env.REACT_APP_API_URL;
    const navigate = useNavigate();
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [orderBy, setOrderBy] = useState('CustomerID');
    const [search, setSearch] = useState('');
    const [filters, setFilters] = useState({});
    const [selectedOptions, setSelectedOptions] = useState('');
    const medal = ['🥇', '🥈', '🥉'];

    const fetchTopCustomers = async () => {
        try {
            const response = await axiosInstance.get(`${apiUrl}/api/getcustomer-top`);
            if (response.status === 200) {
                setTopCustomers(response.data);
            } else {
                console.error('Failed to fetch data:', response.status);
            }
        } catch (error) {
            console.error('Failed to fetch data:', error);
        }
    };

    useEffect(() => {
        fetchTopCustomers();
    }, []);

    const { loading, error, data, refetch } = useQuery(queries.GETALLCUSTOMER, {
        variables: {
            page,
            pageSize,
            orderBy,
            search,
            filters
        },
    });

    useEffect(() => {
        refetch();
    }, [filters, page, pageSize, orderBy]);

    if (error) return <p>{t('Error:')}{error.message}</p>;

    const customers = data?.allCustomer?.data || [];
    const totalPageNum = data?.allCustomer?.totalPageNum || 1;
    const totalCount = data?.allCustomer?.totalCount || 0;

    const Podium = ({ topCustomers }) => (
        // <SimpleBar style={{ maxHeight: 300 }}>
        (<div className="podium-container">
            {/* <SimpleBar style={{ maxHeight: '100%' }}> */}
            <div className="podium">
                {topCustomers.map((customer, index) => (
                    <div className='customer-data-chart-item' key={index}>
                        <span>
                            {medal[index]}
                            ${customer.MoneySpent}
                        </span>
                        <div className={`rank rank-${index + 1}`}>
                            <span>
                                {customer.FirstName}
                            </span>
                        </div>
                        <div className="tooltip">
                            <p>{t('Name:')}{customer.FirstName} {customer.LastName}</p>
                            <p>{t('Spent: $')}{customer.MoneySpent}</p>
                        </div>
                    </div>
                ))}
            </div>
            {/* </SimpleBar> */}
        </div>)
    );

    const handleRowClick = (row) => {
        navigate(`/admin/CustomerDetail/${row.CustomerID}?origin=/admin/CustomerData`);
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    const handlePageSizeChange = (newPageSize) => {
        setPageSize(newPageSize);
    };

    const handleHeaderClick = (field) => {
        let sortField = field;
        if (orderBy.includes(field)) {
            // If already sorted by this field, toggle between asc and desc
            sortField = orderBy.startsWith('-') ? field : '-' + field;
        }
        setOrderBy(sortField);
    };

    const handleSearch = (filter, searchTerm) => {
        if (filter) {
            setFilters(filter);
        } else {
            setFilters({});
        }

        setSearch(searchTerm);
        setPage(1);
    };

    const handleReset = () => {
        setSearch('');
        setFilters({});
        setSelectedOptions('');
    };

    const handleSearchByChange = (newSearchBy, oldSearchBy) => {
        setSelectedOptions(newSearchBy);
        if (newSearchBy === '') {
            setFilters((prevFilters) => {
                const { [`${oldSearchBy}Ilike`]: _, ...rest } = prevFilters;
                return rest;
            });
            return;
        }
        const newFilters = { ...filters, [`${newSearchBy}Ilike`]: search };
        delete newFilters[`${oldSearchBy}Ilike`];
        setFilters(newFilters)
    };

    const headers = [
        { label: 'Customer Code', field: 'AuctionFlexCode' },
        { label: 'FirstName', field: 'FirstName' },
        { label: 'LastName', field: 'LastName' },
        { label: 'Phone', field: 'Phone' },
        { label: 'Email', field: 'Email' },
    ];

    const searchOptions = [
        { value: '', label: 'All' },
        { value: 'FirstName', label: 'First Name' },
        { value: 'LastName', label: 'Last Name' },
        { value: 'Phone', label: 'Phone' },
        { value: 'Email', label: 'Email' },
    ];

    return (
        (<div className='main-container customer-data-page'>
            <div>
                <h1>{t('Top Customers')}</h1>
                <Podium topCustomers={topCustomers} />
            </div>
            <div className='gray-block'>
                <h2>{t('Customer Data')}</h2>
                <h3 className='total-part'><label>{t('Total:')}</label>  <span className='inventory-check-result'>
                    {totalCount}
                </span>
                </h3>
                <div className='customer-data-table-without-title'>
                    <SearchBarGraph
                        onSearch={handleSearch}
                        onReset={handleReset}
                        searchTerm={search}
                        searchOptions={searchOptions}
                        selectedOptions={selectedOptions}
                        onSearchByChange={handleSearchByChange}
                    />
                    <div className='table-container'>
                        <DataTable
                            headers={headers}
                            data={customers}
                            onHeaderClick={handleHeaderClick}
                            onRowClick={handleRowClick}
                            containerClassName={'table-outer-pagination'}
                            loading={loading}
                        />
                        <Pagination
                            page={page}
                            pageSize={pageSize}
                            totalPageNum={totalPageNum}
                            containerClassName={'pagination-container-sticky'}
                            totalCount={totalCount}
                            onPageChange={handlePageChange}
                            onPageSizeChange={handlePageSizeChange}
                        />
                    </div>
                </div>
            </div>
        </div>)
    );
}

export default CustomerData;